import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { HomeLayout } from '../../lib/home-schema.js';
import { useEditMode } from '../home/edit-context.js';
import { AskDrawer } from './AskDrawer.js';
import { NotesDrawer } from './NotesDrawer.js';

interface TopBarProps {
  layout: HomeLayout;
  onMenuClick?: () => void;
}

export function TopBar({ layout, onMenuClick }: TopBarProps) {
  const navigate = useNavigate();
  const { editing, toggle } = useEditMode();
  const [search, setSearch] = useState('');
  const [askOpen, setAskOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  // Cmd/Ctrl+K → focus search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        document.getElementById('sfp-search')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/documents?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <>
      <div className="flex h-14 items-center gap-3 border-b border-stone-200 bg-white px-4">
        {onMenuClick && (
          <button
            type="button"
            onClick={onMenuClick}
            aria-label="Toggle sidebar"
            className="rounded p-1.5 text-stone-500 hover:bg-stone-100 hover:text-stone-800"
          >
            ☰
          </button>
        )}
        <form onSubmit={onSearchSubmit} className="flex-1 max-w-2xl">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">⌕</span>
            <input
              id="sfp-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search or ask ${layout.brand.name}…`}
              className="h-9 w-full rounded-full border border-stone-200 bg-stone-50 pl-9 pr-12 text-sm focus:border-stone-500 focus:bg-white focus:outline-none"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-stone-300 px-1.5 py-0.5 text-[10px] text-stone-500">
              ⌘K
            </span>
          </div>
        </form>

        <div className="flex items-center gap-2">
          {layout.topActions.map((action) => (
            <TopActionButton
              key={action.id}
              label={action.label}
              accent={action.kind === 'ask' ? 'primary' : action.kind === 'edit' ? 'outline' : 'ghost'}
              active={action.kind === 'edit' && editing}
              onClick={() => {
                if (action.kind === 'edit') toggle();
                else if (action.kind === 'ask') setAskOpen(true);
                else if (action.kind === 'notes') setNotesOpen(true);
                else if (action.kind === 'link' && action.href) {
                  if (action.href.startsWith('http')) window.open(action.href, '_blank');
                  else navigate(action.href);
                }
              }}
            />
          ))}
        </div>
      </div>

      {askOpen && <AskDrawer onClose={() => setAskOpen(false)} brand={layout.brand.name} />}
      {notesOpen && <NotesDrawer onClose={() => setNotesOpen(false)} />}
    </>
  );
}

function TopActionButton({
  label,
  accent,
  active,
  onClick,
}: {
  label: string;
  accent: 'primary' | 'outline' | 'ghost';
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'h-9 rounded-md px-3 text-xs font-medium transition',
        accent === 'primary' && 'bg-[color:var(--sfp-primary)] text-white hover:bg-[color:var(--sfp-primary-hover)]',
        accent === 'outline' && 'border border-stone-300 text-stone-700 hover:border-stone-500 hover:bg-stone-50',
        accent === 'ghost' && 'text-stone-700 hover:bg-stone-100',
        active && 'ring-2 ring-[color:var(--sfp-primary)]',
      )}
    >
      {accent === 'primary' && <span className="mr-1 text-stone-200">+</span>}
      {label}
    </button>
  );
}
